import React, { useEffect, useState, useContext, useRef } from 'react';
import Styled from 'styled-components';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { useRouter } from 'next/router';
import { Trans } from '@lingui/react';
import { debounce } from 'lodash';
import { t } from '@lingui/macro';
import Hero from '@ecosystems/landing_pages/hero/Hero';
import {
  TextWrapper,
  TextWrapperInner,
  Title,
  SubTitle,
  VideoWrapper,
  Disclaimer,
} from '@ecosystems/landing_pages/hero/components';
import { Container, Flex } from '@components/layout/Grid';
import messageStripe from '@components/MessageStripe';
import MetaTags from '@components/MetaTags';
import Search from '@ecosystems/events/Search';
import EventCard from '@ecosystems/events/EventListingCard';
import EventsFilters from '@ecosystems/events/EventsFilter';
import ArrowRightIcon from '@assets/ArrowRightIcon';
import {
  Video,
  useLiveEventsQuery,
  LiveEventSortAttribute,
  LiveEventKind,
  LanguageKind,
  LiveEventsDocument,
  LiveEventsQuery,
  PagedLiveEvents,
} from '@gql/generated';
import Spinner from '@components/Spinner';
import Button from '@components/buttons';
import { UserContext } from '@lib/contexts/UserProvider';
import EmptyResults from '@components/EmptyResults';
import { ParsedUrlQuery } from 'querystring';
import Trailer from '@organisms/video-player/Trailer';
import PricingSection from '@ecosystems/pricing/PricingSection';
import { snackbarVar } from '@lib/apollo/policies';
import { LiveCodesAvailableCard } from '@ecosystems/events/LiveCodesAvailableCard';

const MessageStripe = Styled(messageStripe)`
  p {
    display: flex;
    align-items: center;
    margin: 10px auto;
  }
 
  a {
  display: flex;
  align-items: center;
  
   svg {
     flex-shrink: 0;
     width: 20px;
     height: 20px;
     margin-left: 10px;
    }
  }
`;

const HeroSection = Styled(Hero)`
  ${TextWrapper} {
    padding-right: 8%;
    margin: 0 auto;

    ${TextWrapperInner} {
      max-width: 720px;
    }
    
    ${Title} {
      display: flex;
      align-items: center;
      font-size: 34px;
      color: white;
      line-height: 1.2;
      margin-bottom: 0px;
    }

    ${SubTitle} {
      font-size: 20px;
      color: white;
      line-height: 1.5;
      margin-top: 24px;
      margin-bottom: 24px;
    }

    ${Disclaimer} {
      font-size: 13px;
      color: white;
      margin-top: 10px;
    }

    @media (max-width: ${props => props.theme.viewports.tablet}px) {
      ${Title} {
        font-size: 24px;
      }
      margin-top: 20px;
    }

    @media (max-width: ${props => props.theme.viewports.mobile}px) {
      padding-right: 0;
    }
  }

  ${VideoWrapper} {
    display: flex;
    align-items: center;
    border-radius: 5px;
    overflow: hidden;
  }
`;

const Cards = Styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  grid-column-gap: 20px;
  grid-row-gap: 20px;
`;

const LoadMore = Styled(Button)`
  color: #242424;
  border: 1px solid #979797;
  border-radius: 3px;
  padding: 12px 24px;
  background: none;
`;

const heroVideo = {
  id: '53c57b2b-c38e-432e-9de8-2165e8408183',
  identifier: '73sk',
  content: { title: 'live page trailer' },
  thumbnailUrl:
    'https://yogobe.23video.com/64968561/68827179/468592ff9e3125f8c1d304191cf6e473//livetestimonials1920x1080-1mp4-thumbnail.jpglarge/embed/thumbnail.jpg',
  hls:
    'https://yogobe.23video.com/64968561/68827179/5004611f96a9fce75d7172ae84c44574/video_hls/playlist.m3u8',
  player: {
    hasAccess: true,
    assets: [
      {
        src:
          'https://yogobe.23video.com/64968561/68827179/5004611f96a9fce75d7172ae84c44574/video_mobile_high/livetestimonials1920x1080-1mp4-video.mp4',
        resolution: 180,
        size: 1335979,
      },
      {
        src:
          'https://yogobe.23video.com/64968561/68827179/5004611f96a9fce75d7172ae84c44574/video_medium/livetestimonials1920x1080-1mp4-video.mp4',
        resolution: 360,
        size: 2533181,
      },
      {
        src:
          'https://yogobe.23video.com/64968561/68827179/5004611f96a9fce75d7172ae84c44574/video_hd/livetestimonials1920x1080-1mp4-video.mp4',
        resolution: 720,
        size: 4340728,
      },
      {
        src:
          'https://yogobe.23video.com/64968561/68827179/5004611f96a9fce75d7172ae84c44574/video_1080p/livetestimonials1920x1080-1mp4-video.mp4',
        resolution: 1080,
        size: 10835454,
      },
    ],
  },
} as Video;

const buildFilters = (query: ParsedUrlQuery) => {
  const languages = query?.languages ? [query.languages].flat() : ['en', 'se'];
  return {
    search: (query?.search as string) || '',
    languages: languages as LanguageKind[],
    instructor: (query?.instructor as string) || null,
    kind: (query?.kind as LiveEventKind) || null,
    sortBy: (query?.sortBy as LiveEventSortAttribute) || null,
  };
};

const EventsListings = ({ pageProps }) => {
  const router = useRouter();
  const { query } = router;
  const [filters, setFilters] = useState(buildFilters(query));
  const [currentUser] = useContext(UserContext);
  const [loadingMore, setLoadingMore] = useState(false);
  const { data, loading, fetchMore, refetch } = useLiveEventsQuery({
    variables: {
      page: 1,
      ...(pageProps?.initialFilters || {}),
    },
    ssr: false,
  });

  const firstRender = useRef(true);
  const { current: searchRef } = useRef(
    debounce(
      ({ search, languages, instructor, sortBy, kind }) => {
        refetch({
          search: search === '' ? '*' : search,
          languages,
          instructor,
          sortBy,
          kind,
        });
      },
      500,
      {
        maxWait: 3000,
        leading: false,
        trailing: true,
      }
    )
  );

  useEffect(() => {
    if (!firstRender.current) {
      searchRef({
        search: filters.search,
        languages: filters.languages,
        instructor: filters.instructor,
        sortBy: filters.sortBy,
        kind: filters.kind,
      });
    } else {
      firstRender.current = false;
    }
  }, [
    filters.search,
    filters.languages,
    filters.instructor,
    filters.kind,
    filters.sortBy,
  ]);

  const loadMore = async () => {
    setLoadingMore(true);
    await fetchMore({
      variables: { page: data?.liveEvents?.page + 1 },
    });
    setLoadingMore(false);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      languages: [LanguageKind['En'], LanguageKind['Se']],
      instructor: null,
      sortBy: null,
      kind: null,
    });
    router.replace(
      {
        pathname: router.pathname,
        query: { lang: router.query.lang },
      },
      undefined,
      { shallow: true }
    );
  };

  const updateFilters = (filter, value) => {
    setFilters({ ...filters, [filter]: value });
    const query = {
      ...router.query,
      lang: router.query.lang,
      [filter]: value,
    };
    if (!value) {
      // simplest way to delete from query & no have empty param
      delete query[filter];
    }
    router.replace(
      {
        pathname: router.pathname,
        query: query,
      },
      undefined,
      { shallow: true }
    );
    snackbarVar({
      isSnackBarOpen: true,
      snackBarMessage: t({ id: 'live.listings_page.filter_applied_toaster' }),
    });
  };

  const userResolved = currentUser === null || currentUser?.id;
  const userEvents =
    currentUser?.liveEventsAccess?.map(access => access.liveEvent.slug) || [];

  const liveEventsData = (data?.liveEvents ||
    pageProps?.ssrEvents) as PagedLiveEvents;

  const events = liveEventsData?.data;

  let content;
  if (!events || (loading && !userResolved)) {
    content = (
      <Flex justifyContent="center" alignItems="center" mt={50}>
        <Spinner size={28} spinnerColor="#242424" />
      </Flex>
    );
  } else if (events?.length > 0) {
    content = [
      <Cards key={0}>
        <LiveCodesAvailableCard />
        {events?.map(event => {
          if (event.slug === 'yoga-nidra-for-mios-kundklubb') {
            return null;
          }
          return (
            <EventCard
              className="Live--LiveCard"
              key={event.slug}
              event={event}
              hasAccess={userEvents.includes(event.slug)}
            />
          );
        })}
      </Cards>,
    ];

    if (liveEventsData?.totalCount > liveEventsData?.data?.length) {
      content.push(
        <Flex key={1} justifyContent="center" mt={40}>
          <LoadMore
            appearance="outline"
            onClick={loadMore}
            loading={loadingMore}
          >
            <Trans id="live.listings_page.load_more_btn" />
          </LoadMore>
        </Flex>
      );
    }
  } else {
    content = (
      <EmptyResults
        icon="ygb-icon-playlist-empty-ic"
        description={<Trans id="live.listings_page.no_events_found" />}
        action={
          <Button appearance="link" onClick={clearFilters}>
            <Trans id="live.listings_page.clear_events_filters" />
          </Button>
        }
      />
    );
  }

  return (
    <>
      <MetaTags
        title="LIVE-online i realtid"
        description="Yoga, träna och meditera i realtid - med Yogobe LIVE. Praktisera tillsammans som ett Community under vägledning av Team Yogobe."
        thumbnailUrl="/static/live-meta-image.jpg"
      />
      {/* react re-conciler gets confused when an elements is removed/added in between ssr & client render */}
      {/* we end up with a messed up tree, so we wrap the element in a div, fragment doesnt work... */}
      <div>
        {currentUser?.isNoneTrialPremium ? (
          <></>
        ) : (
          <MessageStripe bg="#C5EDFF" color="#243240" closable={false}>
            <a href="#subscription-offers">
              <Trans id="live.listings_page.banner.subscription_discount_text" />
              <ArrowRightIcon />
            </a>
          </MessageStripe>
        )}
      </div>
      <div style={{ backgroundColor: '#243240', padding: '0 20px' }}>
        <Container flexDirection={'column'} py={40}>
          <HeroSection
            title={
              <Trans
                id="live.listings_page.hero_title"
                components={[<span key="0"></span>]}
              />
            }
            subtitle={<Trans id="live.listings_page.subtitle" />}
            actions={[
              {
                name: <Trans id="live.listings_page.explore_events_btn" />,
                theme: 'default-white',
                href: '#explore-events',
              },
            ]}
            video={
              <Trailer
                {...{
                  video: heroVideo,
                  thumbnailImage: heroVideo?.thumbnailUrl,
                  isLimited: false,
                  autoPlay: false,
                  videoSources: heroVideo?.player?.assets,
                  elementID: 'e54a7231-fd70-4a7b-9a00-3ffbafe19de3',
                }}
              />
            }
            disclaimer={<Trans id="live.listings_page.disclaimer" />}
            reverseColumns
          />
        </Container>
      </div>
      <div id="explore-events">
        <EventsFilters
          languages={filters.languages}
          instructor={filters.instructor}
          sortBy={filters.sortBy}
          kind={filters.kind}
          kinds={data?.liveEvents?.availableKinds}
          onInstructorChange={instructor => {
            updateFilters('instructor', instructor);
          }}
          onLanguageChange={langs => updateFilters('languages', langs)}
          onSortChange={sortBy => updateFilters('sortBy', sortBy)}
          onKindChange={kind => updateFilters('kind', kind)}
        />
        <Container fullWidth px={20}>
          <Container flexDirection="column" mb={80}>
            <Search
              handleChange={value => updateFilters('search', value)}
              placeholder={t`live.listings_page.search_input_placeholder`}
              value={filters.search}
            />
            {content}
          </Container>
        </Container>
        <div>
          {currentUser?.isNoneTrialPremium ? (
            <></>
          ) : (
            <PricingSection id="subscription-offers" />
          )}
        </div>
      </div>
    </>
  );
};

export const getServerSideProps = async ctx => {
  const filters = buildFilters(ctx.query);

  const initialFilters = {
    ...filters,
    search: filters.search === '' ? '*' : filters.search,
  };
  try {
    // apolloClient is also set in ctx.apolloClient by
    const apolloClient = ctx.req.apolloClient as ApolloClient<InMemoryCache>;

    // preload the data
    const { data } = await apolloClient.query<LiveEventsQuery>({
      query: LiveEventsDocument,
      variables: {
        page: 1,
        ...initialFilters,
      },
    });

    return {
      props: {
        ssrEvents: data?.liveEvents,
        initialQuery: ctx.query,
        initialFilters,
      },
    };
  } catch (ex) {
    return {
      props: {
        ssrEvents: null,
        initialQuery: ctx.query,
        initialFilters,
      },
    };
  }
};

export default EventsListings;
